/**
 * FileTypeSelector.tsx
 *
 * File type selection panel that appears when clicking the attachment button.
 * Shows supported file types for the current channel with size limits.
 */

import { FC, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  getSupportedCategories,
  FILE_SIZE_LIMITS,
  FORMAT_DISPLAY,
  FileCategory,
} from "../../config/fileRestrictions"

interface FileTypeSelectorProps {
  channel?: string
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
}

// Map file category to accept attribute for file input
const ACCEPT_MAP: Record<FileCategory, string> = {
  image: "image/jpeg,image/png",
  video: "video/mp4,video/3gpp",
  audio: "audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg",
  document: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf",
  sticker: "image/webp",
}

// Icons for each file category
const ICONS: Record<FileCategory, string> = {
  image: "bi-image",
  video: "bi-camera-video",
  audio: "bi-music-note-beamed",
  document: "bi-file-earmark-text",
  sticker: "bi-emoji-smile",
}

const FileTypeSelector: FC<FileTypeSelectorProps> = ({
  channel,
  onFileSelect,
  disabled = false,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedType, setSelectedType] = useState<FileCategory | null>(null)

  const supportedCategories = getSupportedCategories(channel)

  const handleTypeClick = (category: FileCategory) => {
    setSelectedType(category)
    // Update accept attribute and trigger file input
    if (fileInputRef.current) {
      fileInputRef.current.accept = ACCEPT_MAP[category]
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOpen(false)
    setSelectedType(null)
    onFileSelect(event)
  }

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedType(null)
  }

  return (
    <div className="position-relative">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        style={{ display: "none" }}
        data-testid="file-type-selector-input"
      />

      {/* Attachment button */}
      <button
        type="button"
        className="btn btn-sm btn-icon btn-active-light-primary m-0"
        onClick={handleToggle}
        disabled={disabled}
        title={t("HC.Info.Upload")}
        data-testid="attachment-button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          className="text-primary fs-1 p-0 m-0"
        >
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M12.948 7.308L7.42 12.835a2.607 2.607 0 1 0 3.689 3.688l5.982-5.982a4.548 4.548 0 0 0 0-6.452a4.549 4.549 0 0 0-6.4.065l-6.034 5.918a6.517 6.517 0 0 0 9.215 9.214l7.377-7.312"
          />
        </svg>
      </button>

      {/* File type selection panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ zIndex: 1050 }}
            onClick={handleClose}
          />

          {/* Panel */}
          <div
            className="position-absolute bg-white rounded-3 shadow-lg"
            style={{
              bottom: "calc(100% + 8px)",
              left: 0,
              zIndex: 1051,
              minWidth: "300px",
              maxWidth: "400px",
            }}
            data-testid="file-type-panel"
          >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <span className="fw-bold text-dark">
                {t("HC.File.SelectType")}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light"
                onClick={handleClose}
              >
                <i className="bi bi-x fs-4"></i>
              </button>
            </div>

            {/* File type options */}
            <div className="p-2">
              <div className="d-flex flex-wrap gap-2">
                {supportedCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={`btn btn-light-primary d-flex flex-column align-items-center p-3 flex-grow-1 ${
                      selectedType === category ? "active" : ""
                    }`}
                    style={{ minWidth: "80px", maxWidth: "120px" }}
                    onClick={() => handleTypeClick(category)}
                    data-testid={`file-type-${category}`}
                  >
                    <i className={`bi ${ICONS[category]} fs-1 mb-2`}></i>
                    <span className="fw-semibold text-capitalize small">
                      {t(`HC.File.Type.${category}`, category)}
                    </span>
                    <span className="text-muted" style={{ fontSize: "10px" }}>
                      {FORMAT_DISPLAY[category]}
                    </span>
                    <span className="text-muted" style={{ fontSize: "10px" }}>
                      max {FILE_SIZE_LIMITS[category]}MB
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Channel limitation notice */}
            {supportedCategories.length < 5 && (
              <div className="px-3 pb-3">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  {t("HC.File.ChannelLimitation")}
                </small>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default FileTypeSelector
